<template>
  <div class="agreement-card-root">
    <div class="agreement-card-root__cards">
      <div
        v-for="(item, index) in agreements"
        :key="`${brand}-${item.document || ''}-${item.agreementId || index}`"
        class="agreement-card"
        :class="{ 'agreement-card--eudora': brand === 'eudora' }"
      >
      <div class="agreement-card__body">
        <div class="agreement-card__brand">
          <span class="agreement-card__brand-icon-slot" aria-hidden="true">
            <img
              :src="brand === 'eudora' ? '/img/eudora-icone-card.png' : '/img/boticario-icone-card.png'"
              alt=""
              class="agreement-card__brand-icon"
            />
          </span>
          <strong class="agreement-card__brand-name">{{ brandName }}</strong>
        </div>

        <div v-if="documentKind(item)" class="agreement-card__document">
          <strong class="agreement-card__document-title">{{ documentHeading(item) }}</strong>
          <span class="agreement-card__document-id">{{ documentKind(item) }}: {{ formattedDocument(item) }}</span>
        </div>

        <div v-if="item.agreementId" class="agreement-card__row">
          <span class="agreement-card__label">Número do acordo:</span>
          <strong class="agreement-card__value">{{ item.agreementId }}</strong>
        </div>

        <div v-if="titlesText(item)" class="agreement-card__row agreement-card__row--titles">
          <span class="agreement-card__label agreement-card__label--nowrap">Título(s) negociados:</span>
          <span class="agreement-card__titles-value">
            {{ expanded[index] ? titlesText(item) : truncated(titlesText(item), 18) }}
          </span>
          <button
            v-if="canExpandTitles(item)"
            type="button"
            class="agreement-card__expand"
            :title="expanded[index] ? 'Recolher' : 'Expandir'"
            @click="toggle(index)"
          >
            <i :class="expanded[index] ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" />
          </button>
        </div>

        <div class="agreement-card__row">
          <span class="agreement-card__label">Valor do acordo:</span>
          <strong class="agreement-card__value">{{ agreementTotalValueFormatted(item) }}</strong>
        </div>

        <div class="agreement-card__row">
          <span class="agreement-card__label">Plano:</span>
          <strong class="agreement-card__value">{{ planLabel(item) }}</strong>
        </div>

        <div v-if="planLabel(item) === 'À vista'" class="agreement-card__row">
          <span class="agreement-card__label">{{ valueRowLabel(item) }}</span>
          <strong class="agreement-card__value">{{ paymentValueFormatted(item) }}</strong>
        </div>

        <div v-if="planLabel(item) !== 'À vista' && entryFormatted(item)" class="agreement-card__row">
          <span class="agreement-card__label">Entrada:</span>
          <strong class="agreement-card__value">{{ entryFormatted(item) }}</strong>
        </div>

        <div v-if="planLabel(item) !== 'À vista' && installmentsLabel(item)" class="agreement-card__row">
          <span class="agreement-card__label">Parcelas:</span>
          <strong class="agreement-card__value">{{ installmentsLabel(item) }}</strong>
        </div>

        <div class="agreement-card__row">
          <span class="agreement-card__label">Vencimento:</span>
          <strong class="agreement-card__value">{{ dueDateFormatted(item) }}</strong>
        </div>

        <div v-if="paymentMethodLabel(item)" class="agreement-card__row">
          <span class="agreement-card__label">Forma de pagamento:</span>
          <strong class="agreement-card__value">{{ paymentMethodLabel(item) }}</strong>
        </div>
      </div>

      <div class="agreement-card__actions">
        <button
          type="button"
          class="agreement-card__btn"
          :class="{ 'agreement-card__btn--eudora': brand === 'eudora' }"
          :disabled="anyAgreementBillLoading"
          :aria-busy="isLoadingAgreement(item) ? 'true' : 'false'"
          @click="onSecondBill(item)"
        >
          {{ isLoadingAgreement(item) ? 'CARREGANDO…' : '2ª VIA DO BOLETO' }}
        </button>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatData } from '@/utils/helpers';

export default {
  name: 'AgreementCard',
  props: {
    agreements: { type: Array, required: true },
    brand: { type: String, default: 'boticario' },
    agreementBillLoadingKey: { type: String, default: null },
  },
  data: () => ({ expanded: {} }),
  computed: {
    brandName() {
      return this.brand === 'eudora' ? 'Eudora' : 'Boticário';
    },
    anyAgreementBillLoading() {
      return this.agreementBillLoadingKey != null && String(this.agreementBillLoadingKey).length > 0;
    },
  },
  methods: {
    onSecondBill(item) {
      this.$emit('request-second-bill', { item, brand: this.brand });
    },
    agreementRowLoadingKey(item) {
      if (!item || item.agreementId == null || String(item.agreementId).trim() === '') return null;
      return `${this.brand}:${String(item.agreementId).trim()}`;
    },
    isLoadingAgreement(item) {
      const k = this.agreementRowLoadingKey(item);
      if (!k || this.agreementBillLoadingKey == null) return false;
      return this.agreementBillLoadingKey === k;
    },
    documentDigits(item) {
      return String(item?.document || '').replace(/\D/g, '');
    },
    documentKind(item) {
      const type = String(item?.documentType || '').trim().toUpperCase();
      if (type === 'CPF' || type === 'CNPJ') return type;
      const digits = this.documentDigits(item);
      if (digits.length === 14) return 'CNPJ';
      if (digits.length === 11) return 'CPF';
      return '';
    },
    documentHeading(item) {
      const kind = this.documentKind(item);
      if (kind === 'CNPJ') return 'Débitos CNPJ (Pessoa jurídica)';
      if (kind === 'CPF') return 'Débitos CPF (Pessoa física)';
      return '';
    },
    formattedDocument(item) {
      const d = this.documentDigits(item);
      const kind = this.documentKind(item);
      if (kind === 'CNPJ') {
        const padded = d.padStart(14, '0').slice(-14);
        return padded.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
      }
      if (kind === 'CPF') {
        const padded = d.padStart(11, '0').slice(-11);
        return padded.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
      }
      return d;
    },
    titlesText(item) {
      return item.installments || item.contracts || '';
    },
    canExpandTitles(item) {
      return String(this.titlesText(item) || '').length > 28;
    },
    planLabel(item) {
      const currentInstallmentNumber = Number(item.currentInstallmentNumber);
      const installmentNumberAgreement = Number(item.installmentNumberAgreement);
      if (!Number.isNaN(currentInstallmentNumber) && !Number.isNaN(installmentNumberAgreement)) {
        if (currentInstallmentNumber === 0 && installmentNumberAgreement === 0) return 'À vista';
        return 'Parcelado';
      }
      const n = String(item.formName || '').toUpperCase();
      if (n.includes('PARCEL')) return 'Parcelado';
      if (n.includes('VISTA') || n.includes('À VISTA') || n.includes('A VISTA')) return 'À vista';
      const num = Number(item.installmentNumber);
      if (!Number.isNaN(num) && num > 1) return 'Parcelado';
      return 'À vista';
    },
    valueRowLabel(item) {
      return this.planLabel(item) === 'À vista' ? 'Valor do pagamento:' : 'Valor atualizado:';
    },
    paymentValueFormatted(item) {
      const f = item.updatedValueFormatted != null ? String(item.updatedValueFormatted).trim() : '';
      if (f) return `R$ ${f}`;
      if (item.updatedValue != null && !Number.isNaN(Number(item.updatedValue))) {
        return Number(item.updatedValue).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      }
      return '—';
    },
    agreementTotalValueFormatted(item) {
      const installmentNumberAgreement = Number(item.installmentNumberAgreement);
      const updatedValue = Number(item.updatedValue);
      if (!Number.isNaN(updatedValue) && !Number.isNaN(installmentNumberAgreement) && installmentNumberAgreement > 1) {
        return (updatedValue * installmentNumberAgreement).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      }
      return this.paymentValueFormatted(item);
    },
    entryFormatted(item) {
      if (this.planLabel(item) !== 'À vista') {
        const totalInstallments = Number(item.installmentNumberAgreement);
        if (!Number.isNaN(totalInstallments) && totalInstallments > 0) {
          return this.paymentValueFormatted(item);
        }
      }
      const v = item.entryValueFormatted ?? item.minimumEntryValueFormatted;
      if (v == null || v === '') return '';
      const s = String(v).trim();
      if (s.startsWith('R$')) return s;
      return `R$ ${s}`;
    },
    installmentsLabel(item) {
      if (this.planLabel(item) !== 'À vista') {
        const installmentNumberAgreement = Number(item.installmentNumberAgreement);
        const installmentNumber = Number(item.installmentNumber);
        const installmentValueFormatted = item.installmentValueFormatted;
        const installmentsCount = (!Number.isNaN(installmentNumberAgreement) && installmentNumberAgreement > 1)
          ? installmentNumberAgreement
          : ((!Number.isNaN(installmentNumber) && installmentNumber > 0) ? installmentNumber : 0);
        if (installmentsCount > 0) {
          const installmentValue = installmentValueFormatted != null && String(installmentValueFormatted).trim() !== ''
            ? `R$ ${String(installmentValueFormatted).replace(/^R\$\s?/i, '')}`
            : this.paymentValueFormatted(item);
          return `+ ${installmentsCount}x de ${installmentValue}`;
        }
      }
      const n = item.installmentNumber;
      const iv = item.installmentValueFormatted;
      if (n != null && iv != null) return `${n}x de R$ ${String(iv).replace(/^R\$\s?/i, '')}`;
      return '';
    },
    dueDateFormatted(item) {
      const raw = item.earliestDueDate || item.dueDate;
      if (!raw) return '—';
      return formatData(raw, 'YYYY-MM-DD', 'L');
    },
    paymentMethodLabel(item) {
      const raw = String(
        item.negotiationFormPg
        || item.paymentMethod
        || item.formaPagamento
        || ''
      ).trim().toUpperCase();
      if (!raw) return null;
      if (raw === 'B' || raw === 'BOLETO') return 'Boleto';
      if (raw === 'P' || raw === 'PIX') return 'PIX';
      if (raw === 'L' || raw.includes('CART')) return 'Cartão de crédito';
      return null;
    },
    truncated(text, length) {
      const s = String(text || '');
      if (s.length <= length) return s;
      return `${s.substring(0, length)}...`;
    },
    toggle(index) {
      this.$set(this.expanded, index, !this.expanded[index]);
    },
  },
};
</script>

<style scoped>
.agreement-card-root {
  width: 100%;
  min-width: 300px;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
}

.agreement-card-root__cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.agreement-card {
  width: 100%;
  background: #fff;
  border: none;
  border-top: 4px solid #06a472;
  border-radius: 20px;
  box-shadow: var(
    --negotiation-card-shadow,
    0 1px 2px rgba(0, 0, 0, 0.04),
    0 8px 18px -6px rgba(0, 0, 0, 0.13)
  );
  box-sizing: border-box;
  overflow: hidden;
}

.agreement-card--eudora {
  border-top-color: #613a53;
}

.agreement-card__body {
  padding: 16px 20px 12px;
}

.agreement-card__actions {
  padding: 0 20px 18px;
}

.agreement-card__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.agreement-card__document {
  margin: -4px 0 12px;
}

.agreement-card__document-title {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.35;
  color: #1e1e24;
}

.agreement-card__document-id {
  display: block;
  margin-top: 2px;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
  font-weight: 400;
  line-height: 1.35;
  color: #333438;
}

.agreement-card__brand-icon-slot {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.agreement-card__brand-icon {
  width: 100%;
  height: 100%;
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
  object-position: center;
  display: block;
}

.agreement-card__brand-name {
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e1e24;
}

.agreement-card__row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
  line-height: 1.4;
}

.agreement-card__row--titles {
  align-items: flex-start;
}

.agreement-card__label {
  flex-shrink: 0;
  color: #666;
  font-weight: 400;
}

.agreement-card__label--nowrap {
  white-space: nowrap;
}

.agreement-card__value {
  color: #1e1e24;
  font-weight: 700;
}

.agreement-card__titles-value {
  flex: 1 1 0;
  min-width: 0;
  color: #1e1e24;
  font-weight: 400;
  word-break: break-word;
}

.agreement-card__expand {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #176e53;
  padding: 0 4px;
  margin: 0 -4px 0 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1.4;
  min-height: 1.4em;
  border-radius: 4px;
}

.agreement-card__expand:hover {
  background-color: rgba(23, 110, 83, 0.08);
}

.agreement-card__expand:focus {
  outline: none;
}

.agreement-card__expand i {
  font-size: 0.85rem;
}

.agreement-card--eudora .agreement-card__expand {
  color: #613a53;
}

.agreement-card--eudora .agreement-card__expand:hover {
  background-color: rgba(97, 58, 83, 0.1);
}

.agreement-card__btn {
  width: 100%;
  border: none;
  border-radius: 8px;
  background: #06a472;
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  min-height: 48px;
  cursor: pointer;
}

.agreement-card__btn:hover:not(:disabled) {
  background: #125c44;
}

.agreement-card__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.agreement-card__btn--eudora {
  background: #613a53;
}

.agreement-card__btn--eudora:hover:not(:disabled) {
  background: #4d2e42;
}

@media (min-width: 768px) {
  .agreement-card-root {
    width: fit-content;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  .agreement-card-root__cards {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: stretch;
  }

  .agreement-card-root__cards .agreement-card {
    flex: 0 1 360px;
    max-width: 360px;
    min-width: 300px;
    width: 100%;
    box-sizing: border-box;
  }
}

@media (max-width: 767px) {
  .agreement-card-root {
    min-width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
}
</style>
